import { FC, useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createColumnHelper } from '@tanstack/react-table';
import { useForm, Controller } from 'react-hook-form';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import useAdminUsers, { type AdminUser } from '@/api/admin/useAdminUsers.ts';
import useUpdateUserRole from '@/api/admin/useUpdateUserRole.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

dayjs.extend(relativeTime);

const ROLE_OPTIONS = ['user', 'admin', 'auditor'] as const;
type Role = (typeof ROLE_OPTIONS)[number];

const columnHelper = createColumnHelper<AdminUser>();

function formatJoined(dateStr: string) {
  return dayjs(dateStr).format('MMM D, YYYY');
}

type RoleCellProps = {
  userId: string;
  currentRole: Role;
  isSelf: boolean;
  isUpdating: boolean;
  onUpdate: (userId: string, newRole: Role, onError: () => void) => void;
};

const RoleCell: FC<RoleCellProps> = ({ userId, currentRole, isSelf, isUpdating, onUpdate }) => {
  const { control, reset } = useForm<{ role: Role }>({ defaultValues: { role: currentRole } });

  if (isSelf) {
    return <span className="font-dm text-sm text-[#2b3440]">{currentRole}</span>;
  }

  return (
    <Controller
      control={control}
      name="role"
      render={({ field }) => (
        <div className="flex items-center gap-2">
          <SelectInput
            value={field.value}
            options={ROLE_OPTIONS.map((r) => ({ value: r, label: r }))}
            onChange={(newRole) => {
              field.onChange(newRole);
              onUpdate(userId, newRole as Role, () => reset({ role: currentRole }));
            }}
            disabled={isUpdating}
            size="sm"
            bordered
            wrapperClassName="w-auto min-w-[110px]"
            className="font-dm text-[13px]"
          />
          {isUpdating && <span className="loading loading-spinner loading-xs text-[#1b3a6b]" />}
        </div>
      )}
    />
  );
};

const AdminUsersPage: FC = () => {
  const { data: users = [], isLoading } = useAdminUsers();
  const { mutate: updateRole } = useUpdateUserRole();
  const { session } = useAuthContext();
  const currentUserId = session?.user?.id;
  const [filter, setFilter] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const handleRoleUpdate = useCallback(
    (userId: string, newRole: Role, onError: () => void) => {
      setUpdatingUserId(userId);
      updateRole(
        { user_id: userId, new_role: newRole },
        {
          onSettled: () => setUpdatingUserId(null),
          onError,
        }
      );
    },
    [updateRole]
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) ||
        u.organization_name?.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q)
    );
  }, [users, filter]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: () => 'Email',
        cell: (info) => <span className="font-dm font-bold text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
      }),
      columnHelper.accessor('organization_name', {
        header: () => 'Operating Entity',
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
      }),
      columnHelper.accessor('role', {
        header: () => 'Role',
        cell: (info) => (
          <RoleCell
            userId={info.row.original.id}
            currentRole={info.row.original.role as Role}
            isSelf={info.row.original.id === currentUserId}
            isUpdating={updatingUserId === info.row.original.id}
            onUpdate={handleRoleUpdate}
          />
        ),
      }),
      columnHelper.accessor('brand_role', {
        header: () => 'Brand Role',
        enableSorting: false,
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
      }),
      columnHelper.accessor('onboarding_complete', {
        header: () => 'Onboarding',
        enableSorting: true,
        cell: (info) =>
          info.getValue() ? (
            <span className="font-dm font-bold text-sm text-[#00a96e]">Complete</span>
          ) : (
            <span className="font-dm text-sm text-[#ffbe00]">Pending</span>
          ),
      }),
      columnHelper.accessor('decision_count', {
        header: () => 'Governance Events',
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue()}</span>,
      }),
      columnHelper.accessor('created_at', {
        header: () => 'Joined',
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{formatJoined(info.getValue())}</span>,
      }),
    ],
    [currentUserId, updatingUserId, handleRoleUpdate]
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">Decision Participants</h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            View and monitor decision participant activity across COMMAND governed decision lifecycles.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <input
          type="text"
          placeholder="Filter by email, organization or role…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="font-dm text-sm px-4 py-2 border border-[rgba(204,204,204,0.8)] rounded-lg w-full max-w-sm outline-none focus:border-[#1b3a6b] transition-colors"
        />

        {isLoading ? (
          <LoaderWrapper isLoading />
        ) : filtered.length === 0 ? (
          <p className="font-dm text-sm text-center text-[#2b3440]/50 py-10">No decision participants found.</p>
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            zebra
            className="table-auto"
            wrapperClassName="w-full rounded-2xl border border-base-300 bg-base-100"
          />
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
